// React adapter over the module singleton in ./player.
// Safe under StrictMode: initPlayer() is idempotent (caches its own
// promise), so the doubled mount effect does not re-boot the engine.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  initPlayer,
  pause as pauseEngine,
  play as playCode,
  restart as restartCode,
  resume as resumeEngine,
  stop as stopEngine,
} from './player';
import type { PlayerPhase, StatusHandler } from './types';

type UseStrudelOptions = {
  onStatus?: StatusHandler;
  autoInit?: boolean;
};

type UseStrudelResult = {
  phase: PlayerPhase;
  error: Error | null;
  /** Throws synchronously on failure — caller decides how to surface it. */
  play: (code: string) => void;
  /** Like play(), but stops the cyclist and resets the cycle counter to
   *  0 first, so arrange() / firstOf() / etc. start from the top. */
  restart: (code: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
};

export function useStrudel(options: UseStrudelOptions = {}): UseStrudelResult {
  const { onStatus, autoInit = true } = options;
  const [phase, setPhase] = useState<PlayerPhase>('idle');
  const [error, setError] = useState<Error | null>(null);

  // Status handler swap without re-running init.
  const statusRef = useRef<StatusHandler | undefined>(onStatus);
  statusRef.current = onStatus;

  useEffect(() => {
    if (!autoInit) return;
    let cancelled = false;
    setPhase('loading');
    initPlayer((msg, level) => statusRef.current?.(msg, level))
      .then(() => {
        if (!cancelled) setPhase('ready');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        setPhase('error');
        statusRef.current?.('Init failed: ' + err.message, 'err');
      });
    return () => {
      cancelled = true;
    };
  }, [autoInit]);

  // Phase is derived client-side because the scheduler's `started` flag
  // can't distinguish paused from stopped — we track pause explicitly.
  const play = useCallback((code: string) => {
    playCode(code);
    setPhase('playing');
  }, []);

  const restart = useCallback((code: string) => {
    restartCode(code);
    setPhase('playing');
  }, []);

  const pause = useCallback(() => {
    pauseEngine();
    setPhase('paused');
  }, []);

  const resume = useCallback(() => {
    resumeEngine();
    setPhase('playing');
  }, []);

  const stop = useCallback(() => {
    stopEngine();
    // Only transition from an active-audio phase. If the engine is
    // still loading or errored, leave the phase alone so the UI
    // doesn't pretend it's ready.
    setPhase((current) =>
      current === 'playing' || current === 'paused' || current === 'ready'
        ? 'ready'
        : current,
    );
  }, []);

  return { phase, error, play, restart, pause, resume, stop };
}
